/* eslint-disable no-magic-numbers */
/* eslint-disable unicorn/prevent-abbreviations */

import { Table, type TableProps } from '@omnipresentgroup/design-system';
import { useTimeOffsQuery } from '@omnipresentgroup/domains';
import { type TimeOff, TimeOffStatus } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactElement, useEffect, useState } from 'react';

import { ColumnHeaders, useTimeOffsTableConfig } from './hooks';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EMPTY_ARRAY: any[] = [];
const LOADING_ARRAY = Array.from({ length: 10 }, (_value, index) => ({
  id: index,
}));

const DEFAULT_SORT: { id: keyof typeof ColumnHeaders; desc: boolean }[] = [
  {
    id: 'startDate',
    desc: true,
  },
];

type BaseProps = {
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders | string)[];
  optionsOverride?: Omit<TableOptions<TimeOff>, 'data' | 'columns'>;
  columnsOverride?: ColumnDef<TimeOff, unknown>[];
  emptyStateProps?: TableProps['emptyStateProps'];
  onSelectedRowsChange?: (selectedRowIds: string[]) => void;
  onRowClick?: (row: TimeOff) => void;
  useRowSelection?: boolean;
  tableActions?: TableProps['tableActions'];
  showHeader?: boolean;
  filterSection?: ReactElement;
};

type Props = BaseProps &
  (
    | { employeeId: string; companyId?: string }
    | { employeeId?: string; companyId: string }
  );

export const TimeOffs = ({
  employeeId,
  companyId,
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  columnsOverride,
  emptyStateProps,
  onSelectedRowsChange,
  onRowClick,
  useRowSelection,
  tableActions,
  showHeader = true,
  filterSection,
}: Props) => {
  const [rowSelection, setRowSelection] = useState({});

  const {
    data: timeOffs,
    isLoading,
    isError,
  } = useTimeOffsQuery({
    employeeId,
    companyId,
  });

  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORT);

  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const { columns } = useTimeOffsTableConfig({
    columnsOverride,
  });

  useEffect(() => {
    onSelectedRowsChange?.(Object.keys(rowSelection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const tableOptions: Omit<TableOptions<TimeOff>, 'data' | 'columns'> = {
    state: {
      columnVisibility: columnVisibilityState,
      columnOrder,
      rowSelection,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibilityState,
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: (row) =>
      row.original.status !== TimeOffStatus.deleted && !isLoading,
    ...optionsOverride,
  };

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : (timeOffs ?? EMPTY_ARRAY),
    columns,
    getRowId: (row: TimeOff) => row.id,
    ...tableOptions,
  });

  return (
    <Table
      id="timeoffs-list"
      table={table}
      loading={isLoading}
      showErrorState={isError}
      useSorting
      usePagination
      onRowClick={
        onRowClick
          ? (row: TimeOff) => {
              !isLoading && !isError && onRowClick(row);
            }
          : undefined
      }
      emptyStateProps={emptyStateProps}
      useRowSelection={useRowSelection}
      tableActions={tableActions}
      showHeader={showHeader}
      filterSection={filterSection}
    />
  );
};
